'use client';

import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PwaInstallState {
  // False until the client-side mount effect has resolved real platform/
  // display-mode state — same pattern as LanguageContext.mounted, so
  // consumers can avoid rendering an install CTA before it's known
  // whether it even applies (SSR has no window/navigator to check).
  mounted: boolean;
  // display-mode: standalone (Android/Chromium/desktop) OR iOS Safari's
  // legacy navigator.standalone flag — true once the app is already
  // installed and running as its own app, not a browser tab.
  isStandalone: boolean;
  // Conservative UA check used ONLY to choose the "Add to Home Screen"
  // instruction-sheet path — iOS has no beforeinstallprompt under any
  // browser engine (all iOS browsers use WebKit), so this is the one
  // platform distinction that can't be made via a feature check.
  isIOS: boolean;
  // Minimal UA check, used only alongside canInstallAndroid to keep the
  // phone-specific install card off desktop Chrome/Edge — those browsers
  // also fire beforeinstallprompt (desktop apps are installable too),
  // but this product's install card is phone-Home-Screen-worded and
  // business-dashboard-only, so it must not appear there.
  isAndroid: boolean;
  // True once Chrome/Chromium has fired beforeinstallprompt and it
  // hasn't been consumed yet — the only reliable signal that a native
  // install prompt can actually be shown right now.
  canInstallAndroid: boolean;
  promptInstall: () => Promise<void>;
}

// Shared install-eligibility/state hook for the dashboard install card and
// the post-registration success screen — one source of truth so both
// surfaces agree on standalone/platform detection and never duplicate the
// beforeinstallprompt wiring.
export function usePwaInstall(): PwaInstallState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const standaloneMql = window.matchMedia('(display-mode: standalone)');
    const computeStandalone = () =>
      standaloneMql.matches || (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    setIsStandalone(computeStandalone());
    setIsIOS(/iPad|iPhone|iPod/.test(window.navigator.userAgent) && !('MSStream' in window));
    setIsAndroid(/Android/.test(window.navigator.userAgent));
    setMounted(true);

    const onStandaloneChange = () => setIsStandalone(computeStandalone());
    standaloneMql.addEventListener('change', onStandaloneChange);

    const onBeforeInstallPrompt = (e: Event) => {
      // Suppress Chrome's default mini-infobar — the dashboard card / the
      // registration-success screen are the only install entry points.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      standaloneMql.removeEventListener('change', onStandaloneChange);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    // Browser's own native confirmation dialog handles accept/dismiss —
    // nothing here needs to brand or react to the specific outcome; the
    // 'appinstalled' listener above is what flips isStandalone to true.
    await deferredPrompt.userChoice;
    // A given deferredPrompt instance can only be used once.
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return {
    mounted,
    isStandalone,
    isIOS,
    isAndroid,
    canInstallAndroid: !!deferredPrompt,
    promptInstall,
  };
}
